import { isEmpty } from 'lodash'

import { batchTypesMap } from 'app/pages/Class/Inventory/constants'
import { sentryLogger } from 'sentry'

import {
  apiEndpoints,
  validateDeleteRequest,
  validateUpdateRequest
} from './common'

import { makeRequestUrl, modifyQueries } from '../app/helpers'
import { get, patch, post, remove } from '../request'

const enrolmentRequestStructure = (data) => ({
  batch: data.batch,
  payment_mode: data.payment_mode,
  fee: data.fee,
  student: data.student,
  status: data.status,
  type: data.type,
  student_type: data.student_type,
  start_date: data.start_date,
  is_no_fee: data.is_no_fee
})

const fetchEnrolment = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.fetchEnrolment, {
        exclude_type: 'make_up_session',
        batch_type: `${batchTypesMap.event}`,
        ...modifyQueries(queries)
      })

  try {
    const response = await get(requestUrl)
    const transformedData = {
      ...response.data,
      results: response.data?.results?.map((enrolment) => ({
        ...enrolment,
        subject_category:
          enrolment?.batch?.course?.subjects?.[0]?.subject_category
      }))
    }
    return transformedData
  } catch (error) {
    sentryLogger(error, 'error in fetching enrolments')
    throw error
  }
}

const fetchmakeUp = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.enrolment, {
        no_page: 'True',
        type: 'make_up_session',
        ...modifyQueries(queries)
      })

  try {
    const response = await get(requestUrl)

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching makeups')
    throw error
  }
}

const fetchExploreSessionEnrolments = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.enrolment, {
        no_page: 'True',
        type: 'infinite',
        program_type: `${batchTypesMap.exploration},${batchTypesMap.exploration_camp}`,
        ...modifyQueries(queries)
      })

  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching explore session enrolments')
    throw error
  }
}
const getToddlerClubProgramTypes = async () => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.programs}/programs`, {
    exclude: 'school,care,excel,exploration_camp'
  })

  try {
    const response = await get(requestUrl)
    return response.data?.results?.map((program) => program.name)
  } catch (error) {
    sentryLogger(error, ' error in fetching toddler club program types')
    throw error
  }
}

const fetchToddlerClubSession = async ({ url, queries } = {}) => {
  const programNames = await getToddlerClubProgramTypes()

  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.enrolment, {
        no_page: 'True',
        type: 'infinite',
        program_name: programNames.join(','),
        ...modifyQueries(queries)
      })

  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    sentryLogger(error, ' error in fetching toddler club sessions')
    throw error
  }
}

const fetchExploreEnrolment = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.programEnrolment, {
        program_type: `${batchTypesMap.exploration},${batchTypesMap.exploration_camp}`,
        ...modifyQueries(queries)
      })

  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    sentryLogger(error, ' error in fetching explore enrolments')
    throw error
  }
}

const fetchToddlerClubEnrolment = async ({ url, queries } = {}) => {
  const programNames = await getToddlerClubProgramTypes()

  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.programEnrolment, {
        program_name: programNames.join(','),
        ...modifyQueries(queries)
      })

  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    sentryLogger(error, ' error in fetching toddler club enrolments')
    throw error
  }
}

const fetchExcelEnrolment = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.programEnrolment, {
        program_type: `${batchTypesMap.excel}`,
        ...modifyQueries(queries)
      })

  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    sentryLogger(error, ' error in fetching excel enrolments')
    throw error
  }
}

const fetchPsAcEnrolment = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.psacenrolments, {
        ...modifyQueries(queries)
      })

  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    sentryLogger(error, ' error in fetching excel enrolments')
    throw error
  }
}

const terminatePSACEnrolment = async (data) => {
  try {
    const response = await post(`${apiEndpoints.psacenrolments}terminate`, {
      end_date: data?.endDate,
      student_admission_uuid: data.student_admission_uuid,
      termination_reason: data.reason,
      termination_notes: data.notes
    })

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in terminate enrolment')
    throw error
  }
}

// Feature 2 — mid-term programme upgrade. POSTs to the per-admission modify
// endpoint and returns the differential invoice payload (UUID + payment link).
const modifyPSACAdmission = async ({
  studentAdmissionUuid,
  newProgramFeeUuid,
  newBatchUuid,
  effectiveDate,
  overrideDowngrade,
  forceGst
}) => {
  try {
    const response = await post(
      `${apiEndpoints.psacenrolments}${studentAdmissionUuid}/modify/`,
      {
        new_program_fee_uuid: newProgramFeeUuid,
        new_batch_uuid: newBatchUuid,
        effective_date: effectiveDate,
        override_downgrade: !!overrideDowngrade,
        force_gst: !!forceGst
      }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in modifying admission')
    throw error
  }
}

// Akshay 2026-05-27 — Phase 2. Preview the modify-admission
// differential breakdown WITHOUT mutating anything or creating an
// Invoice. Same payload shape as modifyPSACAdmission. Response carries
// a cart-style `payment_summary` array the FE renders with the same
// ProgramRupees component.
const previewPSACAdmissionModification = async ({
  studentAdmissionUuid,
  newProgramFeeUuid,
  newBatchUuid,
  effectiveDate,
  overrideDowngrade,
  forceGst
}) => {
  try {
    const response = await post(
      `${apiEndpoints.psacenrolments}${studentAdmissionUuid}/modify/preview/`,
      {
        new_program_fee_uuid: newProgramFeeUuid,
        new_batch_uuid: newBatchUuid,
        effective_date: effectiveDate,
        override_downgrade: !!overrideDowngrade,
        force_gst: !!forceGst
      }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in previewing modify admission')
    throw error
  }
}

// Phase 3 — cancel an awaiting_payment modification. Voids the
// differential Invoice; no admission state to roll back.
const cancelPendingPSACAdmissionModification = async ({
  studentAdmissionUuid,
  invoiceUuid,
  reason
}) => {
  try {
    const response = await post(
      `${apiEndpoints.psacenrolments}${studentAdmissionUuid}/modify/cancel/`,
      { invoice_uuid: invoiceUuid, reason: reason || null }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in cancelling pending modify admission')
    throw error
  }
}

// Feature 5 — AIM-only seat block. Generate the Term Fee invoice for
// a seat-blocked admission. Idempotent: returns the existing invoice if
// one is already linked to the next pending instalment.
const generatePSACTermInvoice = async ({
  studentAdmissionUuid,
  newProgramFeeUuid,
  newBatchUuid,
  effectiveDate,
  forceGst
}) => {
  try {
    const response = await post(
      `${apiEndpoints.psacenrolments}${studentAdmissionUuid}/generate_term_invoice/`,
      {
        // Charmi 2026-05-28 item 2.1 — optional programme change at
        // term-fee time (seat-block conversion to a higher programme).
        new_program_fee_uuid: newProgramFeeUuid || null,
        new_batch_uuid: newBatchUuid || null,
        effective_date: effectiveDate || null,
        force_gst: !!forceGst
      }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in generating Term Fee invoice')
    throw error
  }
}

// Charmi 2026-05-28 item 2.3 — preview the Term-fee-after-AIM payment
// summary WITHOUT creating an invoice. Optional programme change params
// (item 2.1) preview the full new-programme term fee hypothetically.
const previewPSACTermInvoice = async ({
  studentAdmissionUuid,
  newProgramFeeUuid,
  newBatchUuid,
  forceGst
}) => {
  try {
    const response = await post(
      `${apiEndpoints.psacenrolments}${studentAdmissionUuid}/generate_term_invoice/preview/`,
      {
        new_program_fee_uuid: newProgramFeeUuid || null,
        new_batch_uuid: newBatchUuid || null,
        force_gst: !!forceGst
      }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in previewing Term Fee invoice')
    throw error
  }
}

// Feature 5 — Release the seat (terminate the seat-blocked admission).
// AIM is non-refundable (Decision #4).
const releasePSACSeat = async ({ studentAdmissionUuid, notes }) => {
  try {
    const response = await post(
      `${apiEndpoints.psacenrolments}${studentAdmissionUuid}/release_seat/`,
      { notes }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in releasing seat')
    throw error
  }
}

const createEnrolment = async (data) => {
  try {
    const response = await post(
      apiEndpoints.enrolment,
      enrolmentRequestStructure(data)
    )

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in creating enrolment')
    throw error
  }
}

const updateEnrolment = async (data) => {
  validateUpdateRequest(data)

  try {
    const queries = enrolmentRequestStructure(data)
    queries.student = queries.student?.user?.uuid

    const response = await patch(
      `${apiEndpoints.enrolment}/${data.uuid}/`,
      queries
    )

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating enrolment')
    throw error
  }
}

const deleteEnrolment = async ({ ids }) => {
  validateDeleteRequest(ids)

  try {
    await Promise.all(
      ids.map((id) => {
        return remove(`${apiEndpoints.enrolment}/${id}`)
      })
    )

    return ids
  } catch (error) {
    sentryLogger(error, ' error in deleting enrolment')
    throw error
  }
}

const changeEnrolmentBatch = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.ohClasses}/change_enrolment_batch`,
      {
        enrolment_uuid: data.enrolment_uuid,
        batch_uuid: data.batch_uuid
      }
    )

    return response.data
  } catch (error) {
    sentryLogger(error, 'Error in changing batch')
    throw error
  }
}

const fetchDueAmount = async (data) => {
  const { enrolment, month } = data

  try {
    const response = await get(
      `${apiEndpoints.paymentInvoice}?enrolment=${enrolment}&month=${month}&status=due`
    )

    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

const terminateEnrolment = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.ohClasses}/terminate_enrolment`,
      {
        enrolment_uuid: data.enrolment_uuid,
        amount_to_charge: data.amount_to_charge,
        notes: data.notes,
        reason: data.reason
      }
    )

    return response.data
  } catch (error) {
    sentryLogger(error, 'Error in termianting enrolment')
    throw error
  }
}

const getSingleEnrolment = async ({ url, queries, enrolmentId } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(`${apiEndpoints.fetchEnrolment}/${enrolmentId}/`, queries)

  try {
    const response = await get(requestUrl)

    return response.data
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

const getEnrolment = async ({ url, queries, batchId } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(`${apiEndpoints.batch}/${batchId}/`, queries)

  try {
    const response = await get(requestUrl)

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in getting enrolment')
    throw error
  }
}

const getSessions = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(`${apiEndpoints.attendance}`, { limit: 150, ...queries })

  try {
    const response = await get(requestUrl)

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in getting enrolment')
    throw error
  }
}

const cancelSingleSession = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.attendanceV2}${data.sessionId}/cancel/`,
      { ...data.queries }
    )

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in terminate enrolment')
    throw error
  }
}

const getCoinPrediction = async (data) => {
  try {
    const response = await post(`${apiEndpoints.cart}/calculate/`, {
      suggest: [...data],
      parent_id: data[0].parent,
      book_ongoing:
        data[0]?.type === 'explore' || data[0]?.type === 'event' ? true : false
    })

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in terminate enrolment')
    throw error
  }
}

const getCancelReasons = async () => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.configApp}`, {
    key: 'ATTENDANCE_CANCELLATION_REASONS'
  })

  try {
    const response = await get(requestUrl)

    return response.data.results[0].value
  } catch (error) {
    sentryLogger(error, 'error in terminate enrolment')
    throw error
  }
}

const fetchEnrolmentExtensions = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.actions, {
        ...modifyQueries(queries)
      })

  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching enrolment extension details')
    throw error
  }
}
const fetchDeductionReasons = async () => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.configApp}`, {
    key: 'DEDUCTION_REASONS'
  })
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching deduction reasons')
    throw error
  }
}

export {
  cancelSingleSession,
  changeEnrolmentBatch,
  createEnrolment,
  deleteEnrolment,
  fetchDeductionReasons,
  fetchDueAmount,
  fetchEnrolment,
  fetchEnrolmentExtensions,
  fetchExploreEnrolment,
  fetchExcelEnrolment,
  fetchPsAcEnrolment,
  terminatePSACEnrolment,
  modifyPSACAdmission,
  previewPSACAdmissionModification,
  cancelPendingPSACAdmissionModification,
  generatePSACTermInvoice,
  previewPSACTermInvoice,
  releasePSACSeat,
  fetchExploreSessionEnrolments,
  fetchmakeUp,
  getCancelReasons,
  getCoinPrediction,
  getEnrolment,
  getSessions,
  getSingleEnrolment,
  terminateEnrolment,
  updateEnrolment,
  fetchToddlerClubEnrolment,
  fetchToddlerClubSession
}
